import { WarehouseVatRate } from 'woofwoof-api';

/** Backend `WAREHOUSE_MAX_MONEY_MINOR`: 10 000 000,00 zł, the cap of every price and cost. */
export const WAREHOUSE_MAX_MONEY_MINOR = 1000000000;

export function formatMinor(minor: number, currency = 'zł'): string {
  const sign = minor < 0 ? '-' : '';
  const absolute = Math.abs(Math.round(minor));
  const whole = Math.floor(absolute / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const grosze = String(absolute % 100).padStart(2, '0');
  return `${sign}${whole},${grosze} ${currency}`;
}

/**
 * A typed amount (comma or dot, at most 2 decimals) as integer grosze, read
 * from the digits, so a price is never carried through a float multiply
 * (FW-2/2, mirrors `parseQuantityMilli`).
 */
export function parseMoneyToMinor(value: string): number | null {
  const match = /^(\d+)(?:[.,](\d{1,2}))?$/.exec(
    value.trim().replace(/\s/g, ''),
  );
  if (!match) {
    return null;
  }
  const minor =
    Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
  return Number.isSafeInteger(minor) ? minor : null;
}

/** The inverse of `parseMoneyToMinor`: integer digits split, never a float divide. */
export function minorToInput(minor?: number | null): string {
  if (minor === undefined || minor === null) {
    return '';
  }
  const sign = minor < 0 ? '-' : '';
  const absolute = Math.abs(minor);
  const whole = Math.floor(absolute / 100);
  const grosze = String(absolute % 100).padStart(2, '0');
  return `${sign}${whole},${grosze}`;
}

const VAT_PERCENT: Record<WarehouseVatRate, number> = {
  '23': 23,
  '8': 8,
  '5': 5,
  '0': 0,
  ZW: 0,
};

const isMinor = (value: number): boolean =>
  Number.isSafeInteger(value) && value >= 0;

/**
 * `value × multiplier / divisor` rounded half up, exactly, for non-negative
 * safe integers and a small divisor, or null when any step leaves the
 * safe-integer range. `value = whole × divisor + rest`, so the only division
 * is of `rest × multiplier`, which stays small and exact. Exported so a
 * derived price (e.g. `unitPriceFromPackage`'s mirror) never divides a float
 * either (FW-2/2).
 */
export function multiplyDivideHalfUp(
  value: number,
  multiplier: number,
  divisor: number,
): number | null {
  if (!isMinor(value) || !isMinor(multiplier)) {
    return null;
  }
  const rest = value % divisor;
  const whole = (value - rest) / divisor;
  const wholePart = whole * multiplier;
  const doubledRest = 2 * rest * multiplier + divisor;
  if (!Number.isSafeInteger(wholePart) || !Number.isSafeInteger(doubledRest)) {
    return null;
  }
  const restPart =
    (doubledRest - (doubledRest % (2 * divisor))) / (2 * divisor);
  const result = wholePart + restPart;
  return Number.isSafeInteger(result) ? result : null;
}

/**
 * Review R1-1 (#668), mirror of the backend `milliValueMinor`/`lineValueMinor`:
 * `quantityMilli` thousandths at `priceMinor` per whole one, in grosze
 * rounded half up. Integer arithmetic only; null instead of an unsafe value.
 */
export function milliValueMinor(
  quantityMilli: number,
  priceMinor: number,
): number | null {
  return multiplyDivideHalfUp(quantityMilli, priceMinor, 1000);
}

/** Mirror of the backend `grossFromNet`: one half-up rounding of net × (100 + VAT) / 100. */
export function grossFromNetMinor(
  netMinor: number,
  vatRate: WarehouseVatRate,
): number | null {
  return multiplyDivideHalfUp(netMinor, 100 + VAT_PERCENT[vatRate], 100);
}

/** Mirror of the backend `netFromGross`: one half-up rounding of gross × 100 / (100 + VAT). */
export function netFromGrossMinor(
  grossMinor: number,
  vatRate: WarehouseVatRate,
): number | null {
  return multiplyDivideHalfUp(grossMinor, 100, 100 + VAT_PERCENT[vatRate]);
}
